import { Component, EventEmitter, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { UtilityService } from '@services/utility.service';

export interface StructuralDetailsPlant {
    // The plant's stationCode, e.g. "NE=51465290".
    id: string;
    name?: string;
    region?: string;
    city?: string;
}

interface DropdownItem {
    id: number;
    name: string;
}

// At most `places` decimals, matching the API's decimal fields: 2 places for
// angles, 3 for distances/ratings. The sign is left to Validators.min.
function decimalPlaces(places: number): ValidatorFn {
    const pattern = new RegExp(`^-?\\d+(\\.\\d{1,${places}})?$`);
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
            return null;
        }
        return pattern.test(String(value)) ? null : { decimalPlaces: { places } };
    };
}

// The API speaks 'YYYY-MM-DD'; the datepicker speaks Date. Both conversions
// stay in local time so the picked day doesn't shift across UTC.
function toApiDate(date: Date | null): string | null {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromApiDate(value: string | null): Date | null {
    if (!value) {
        return null;
    }
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function notInFuture(control: AbstractControl): ValidationErrors | null {
    const date: Date = control.value;
    if (!(date instanceof Date)) {
        return null;
    }
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return date > endOfToday ? { futureDate: true } : null;
}

// Reusable "Structural Field Details" modal. Drop <app-structural-details-modal>
// into any page, grab it with @ViewChild and call open(plant). It loads the
// dropdowns plus the plant's saved details (if any) and POSTs on Save.
@Component({
    selector: 'app-structural-details-modal',
    templateUrl: './structural-details-modal.component.html'
})
export class StructuralDetailsModalComponent implements OnDestroy {
    @ViewChild('structuralDetailsModal') modalTemplate!: TemplateRef<any>;
    @Output() saved = new EventEmitter<any>();

    plant: StructuralDetailsPlant;
    form: FormGroup;
    loading = false;
    saving = false;
    submitted = false;
    readonly today = new Date();
    readonly datepickerConfig: Partial<BsDatepickerConfig> = {
        showWeekNumbers: false,
        dateInputFormat: 'MM/DD/YYYY',
        adaptivePosition: true
    };

    structureTypes: DropdownItem[] = [];
    structureSubTypes: DropdownItem[] = [];
    materials: DropdownItem[] = [];
    subTypesLoading = false;

    private modalRef: NgbModalRef;
    private close$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private modalService: NgbModal,
        private _utilityService: UtilityService,
        private toastr: ToastrService
    ) {
        this.buildForm();
    }

    ngOnDestroy(): void {
        this.close$.next();
        this.close$.complete();
    }

    open(plant: StructuralDetailsPlant): void {
        if (!plant?.id) {
            this.toastr.error('Plant reference is missing, unable to open structural details.');
            return;
        }
        this.plant = plant;
        this.submitted = false;
        this.structureSubTypes = [];
        this.buildForm();

        this.modalRef = this.modalService.open(this.modalTemplate, {
            size: 'lg',
            backdrop: 'static',
            keyboard: false,
            centered: true
        });
        this.modalRef.result.finally(() => this.close$.next()).catch(() => { });

        this.loadData();
    }

    close(): void {
        this.modalRef?.dismiss('Close click');
    }

    hasError(name: string, error?: string): boolean {
        const control = this.form.get(name);
        if (!control || control.disabled || !(control.touched || this.submitted)) {
            return false;
        }
        return error ? control.hasError(error) : control.invalid;
    }

    onStructureTypeChange(typeId: number): void {
        this.form.patchValue({ structure_sub_type: null });
        this.loadSubTypes(typeId);
    }

    save(): void {
        this.submitted = true;
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.toastr.error('Please fix the highlighted fields.');
            return;
        }

        const v = this.form.getRawValue();
        const num = (value: any) => (value === null || value === undefined || value === '' ? null : Number(value));
        const payload = {
            stationCode: this.plant.id,
            structure_type: v.structure_type,
            structure_sub_type: v.structure_sub_type,
            true_south_alignment: !!v.true_south_alignment,
            azimuth_angle: v.true_south_alignment ? num(v.azimuth_angle) : null,
            structure_angle_tilt: num(v.structure_angle_tilt),
            row_spacing_pitch_distance: num(v.row_spacing_pitch_distance),
            ground_clearance_leg_height: num(v.ground_clearance_leg_height),
            wind_load_design_rating: num(v.wind_load_design_rating),
            structure_to_inverter_distance: num(v.structure_to_inverter_distance),
            material_composition: v.material_composition || null,
            anti_corrosion_coating_thickness: num(v.anti_corrosion_coating_thickness),
            waterproofing_certified: !!v.waterproofing_certified,
            waterproofing_certification_date: v.waterproofing_certified ? toApiDate(v.waterproofing_certification_date) : null
        };

        this.saving = true;
        this._utilityService.saveStructuralFieldDetails(payload).pipe(takeUntil(this.close$)).subscribe({
            next: (res) => {
                this.saving = false;
                if (res?.success === false) {
                    this.toastr.error(res?.message || 'Unable to save structural details.');
                    return;
                }
                this.toastr.success('Structural details saved successfully.');
                this.saved.emit(res?.data ?? payload);
                this.modalRef?.close(res?.data ?? payload);
            },
            error: (err) => {
                this.saving = false;
                this.toastr.error(this.extractError(err) || 'Unable to save structural details.');
            }
        });
    }

    private buildForm(): void {
        this.form = this.fb.group({
            structure_type: [null, Validators.required],
            structure_sub_type: [{ value: null, disabled: true }, Validators.required],
            true_south_alignment: [false],
            azimuth_angle: [{ value: null, disabled: true }, [Validators.required, Validators.min(0), Validators.max(360), decimalPlaces(2)]],
            structure_angle_tilt: [null, [Validators.min(0), Validators.max(90), decimalPlaces(2)]],
            row_spacing_pitch_distance: [null, [Validators.min(0), decimalPlaces(3)]],
            ground_clearance_leg_height: [null, [Validators.min(0), decimalPlaces(3)]],
            wind_load_design_rating: [null, [Validators.min(0), decimalPlaces(3)]],
            structure_to_inverter_distance: [null, [Validators.min(0), decimalPlaces(3)]],
            material_composition: [null],
            anti_corrosion_coating_thickness: [null, [Validators.min(0), decimalPlaces(3)]],
            waterproofing_certified: [false],
            waterproofing_certification_date: [{ value: null, disabled: true }, [Validators.required, notInFuture]]
        });

        // The azimuth and certification date only apply when their toggle is on.
        this.toggleWith('true_south_alignment', 'azimuth_angle');
        this.toggleWith('waterproofing_certified', 'waterproofing_certification_date');
        this.form.get('structure_type').valueChanges.subscribe((typeId) => {
            const subType = this.form.get('structure_sub_type');
            typeId ? subType.enable({ emitEvent: false }) : subType.disable({ emitEvent: false });
        });
    }

    private toggleWith(toggleName: string, controlName: string): void {
        this.form.get(toggleName).valueChanges.subscribe((on: boolean) => {
            const control = this.form.get(controlName);
            if (on) {
                control.enable({ emitEvent: false });
            } else {
                control.reset(null, { emitEvent: false });
                control.disable({ emitEvent: false });
            }
        });
    }

    private loadData(): void {
        this.loading = true;
        const dropdown = (obs) => obs.pipe(catchError(() => of([])));
        forkJoin({
            types: dropdown(this._utilityService.getStructureTypesDropdown()),
            materials: dropdown(this._utilityService.getMaterialCompositionsDropdown()),
            // No saved record yet comes back as an error or empty data; both mean "new".
            details: this._utilityService.getStructuralFieldDetails(this.plant.id).pipe(catchError(() => of(null)))
        })
            .pipe(takeUntil(this.close$))
            .subscribe(({ types, materials, details }: any) => {
                this.structureTypes = types || [];
                this.materials = materials || [];
                const data = details?.data;
                if (data && data.id) {
                    this.patchForm(data);
                }
                this.loading = false;
            });
    }

    private patchForm(data: any): void {
        const num = (value: any) => (value === null || value === undefined || value === '' ? null : Number(value));
        this.form.patchValue({
            structure_type: data.structure_type,
            true_south_alignment: !!data.true_south_alignment,
            waterproofing_certified: !!data.waterproofing_certified
        });
        this.form.patchValue({
            structure_sub_type: data.structure_sub_type,
            azimuth_angle: num(data.azimuth_angle),
            structure_angle_tilt: num(data.structure_angle_tilt),
            row_spacing_pitch_distance: num(data.row_spacing_pitch_distance),
            ground_clearance_leg_height: num(data.ground_clearance_leg_height),
            wind_load_design_rating: num(data.wind_load_design_rating),
            structure_to_inverter_distance: num(data.structure_to_inverter_distance),
            material_composition: data.material_composition,
            anti_corrosion_coating_thickness: num(data.anti_corrosion_coating_thickness),
            waterproofing_certification_date: fromApiDate(data.waterproofing_certification_date)
        });
        if (data.structure_type) {
            this.loadSubTypes(data.structure_type);
        }
    }

    private loadSubTypes(typeId: number): void {
        this.structureSubTypes = [];
        if (!typeId) {
            return;
        }
        this.subTypesLoading = true;
        this._utilityService.getStructureSubTypesDropdown(typeId).pipe(takeUntil(this.close$)).subscribe({
            next: (res) => {
                this.structureSubTypes = res || [];
                this.subTypesLoading = false;
            },
            error: () => {
                this.structureSubTypes = [];
                this.subTypesLoading = false;
            }
        });
    }

    // DRF errors come back either as { message } or as { field: [msg] }.
    private extractError(err: any): string {
        const body = err?.error;
        if (!body) {
            return '';
        }
        if (typeof body.message === 'string' && body.message !== 'success') {
            return body.message;
        }
        const source = body.data && typeof body.data === 'object' ? body.data : body;
        const first = Object.values(source).find((value) => Array.isArray(value) || typeof value === 'string');
        return Array.isArray(first) ? String(first[0]) : (first as string) || '';
    }
}
